"use client";

import { HugeiconsIcon } from "@hugeicons/react";
import { Wallet01Icon } from "@hugeicons-pro/core-solid-rounded";
import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { getUpdateDefaultCurrencyMutationClient } from "@workspace/casino_core/mutations/UpdateDefaultCurrencyMutation.client";
import { getUpdateSbCurrencyMutationClient } from "@workspace/casino_core/mutations/UpdateSbCurrencyMutation.client";
import type { Currency } from "@workspace/casino_core/schema";
import { useRouter } from "next/navigation";
import { Popover, Switch } from "radix-ui";
import type { FC } from "react";
import { useEffect, useState } from "react";
import BalanceDisplay from "../Balance";
import CurrencyDropdownList from "./CurrencyDropdownList";
import type {
  CurrencyPrecision,
  LabeledCurrencyItem,
} from "./getCurrencyDropdownData";
import useHeaderBalances from "./useHeaderBalances";

type Props = {
  brand: string;
  currentCurrency: string;
  currentBalance: number;
  currencyItems: LabeledCurrencyItem[];
  currencyPrecision: Record<string, CurrencyPrecision>;
  depositLabel: string;
  cashierPath: string;
  noBalancesTitle: string;
  noBalancesSubtitle: string;
  hideZeroLabel: string;
  sbUserPreferenceId?: string;
  isUbtcEnabled: boolean;
  originalGameHandles?: string[];
};

const CurrencyDropdownView: FC<Props> = ({
  brand,
  currentCurrency,
  currentBalance,
  currencyItems,
  currencyPrecision,
  depositLabel,
  cashierPath,
  noBalancesTitle,
  noBalancesSubtitle,
  hideZeroLabel,
  sbUserPreferenceId,
  isUbtcEnabled,
  originalGameHandles,
}) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [activeCurrency, setActiveCurrency] = useState(currentCurrency);
  const [hideZeroBalances, setHideZeroBalances] = useState(false);
  const { balances, syncedAt, refresh } = useHeaderBalances({
    brand,
    currentCurrency,
    currentBalance,
    currencyItems,
    originalGameHandles,
  });
  const activeBalance = balances[activeCurrency] ?? 0;
  const items = currencyItems.map((item) => ({
    ...item,
    cashierBalance: balances[item.currency],
  }));

  useEffect(() => {
    const handleCurrencySwitched = () => router.refresh();
    window.addEventListener("currencySwitched", handleCurrencySwitched);
    return () =>
      window.removeEventListener("currencySwitched", handleCurrencySwitched);
  }, [router]);

  const visibleItems = hideZeroBalances
    ? items.filter((item) => item.cashierBalance > 0)
    : items;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) refresh();
  };

  const handleDeposit = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("c", cashierPath);
    window.history.replaceState(window.history.state, "", url.toString());
    window.dispatchEvent(new CustomEvent("openCashier"));
  };

  const handleCurrencySelect = async (item: LabeledCurrencyItem) => {
    const prevCurrency = activeCurrency;

    setActiveCurrency(item.currency);
    setIsOpen(false);

    try {
      await Promise.all([
        getUpdateDefaultCurrencyMutationClient({ currency: item.currency }),
        ...(sbUserPreferenceId
          ? [
              getUpdateSbCurrencyMutationClient({
                input: {
                  currency: item.currency as Currency,
                  id: sbUserPreferenceId,
                },
              }),
            ]
          : []),
      ]);

      router.refresh();
    } catch {
      setActiveCurrency(prevCurrency);
    }
  };

  return (
    <div className="flex h-10 md:h-11 items-center gap-2 rounded-xl border border-border bg-accent px-1 pl-2 fs-exclude">
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="group flex items-center gap-2 cursor-pointer"
            data-testid="header-currency-switcher"
          >
            <BalanceDisplay
              currency={activeCurrency}
              cashierBalance={activeBalance}
              {...currencyPrecision[activeCurrency]}
              syncedAt={syncedAt}
              isUbtcEnabled={isUbtcEnabled}
              originalGameHandles={originalGameHandles}
            />
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              size={18}
              className="text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
            />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="center"
            sideOffset={22}
            className="z-50 w-88 rounded-lg bg-popover p-4 shadow-md ring-1 ring-foreground/10 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
            data-testid="header-currency-switcher-panel"
          >
            <CurrencyDropdownList
              items={visibleItems}
              activeCurrency={activeCurrency}
              onSelect={handleCurrencySelect}
              noBalancesTitle={noBalancesTitle}
              noBalancesSubtitle={noBalancesSubtitle}
              isUbtcEnabled={isUbtcEnabled}
            />
            <div className="flex items-center justify-between px-1 pt-3">
              <span className="text-sm text-muted-foreground">
                {hideZeroLabel}
              </span>
              <Switch.Root
                checked={hideZeroBalances}
                onCheckedChange={setHideZeroBalances}
                className="relative h-6 w-11 cursor-pointer rounded-full bg-foreground/20 transition-colors data-[state=checked]:bg-primary"
              >
                <Switch.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[22px]" />
              </Switch.Root>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <button
        type="button"
        onClick={handleDeposit}
        className="flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-white cursor-pointer"
        data-testid="header-cashier-button"
      >
        <HugeiconsIcon icon={Wallet01Icon} size={15} color="white" />
        <span className="hidden md:block text-[13px] font-medium">
          {depositLabel}
        </span>
      </button>
    </div>
  );
};

export default CurrencyDropdownView;
